import type { FormEvent } from "react"

import type { DateScope, StayScope, VacationSearchCriteria } from "./findVacationsData"

type VacationSearchControlsProps = {
  dateScope: DateScope
  disabled: boolean
  onDateScopeChange: (dateScope: DateScope) => void
  onQueryChange: (query: string) => void
  onStayScopeChange: (stayScope: StayScope) => void
  onSubmit: (search: VacationSearchCriteria) => void
  query: string
  stayScope: StayScope
}

const dateScopeOptions: ReadonlyArray<{ label: string; value: DateScope }> = [
  { label: "Completed", value: "completed" },
  { label: "Upcoming", value: "upcoming" },
  { label: "All time", value: "all_time" },
]

const stayScopeOptions: ReadonlyArray<{ label: string; value: StayScope }> = [
  { label: "This camp", value: "this_camp" },
  { label: "All stays", value: "all_stays" },
]

export function VacationSearchControls({
  dateScope,
  disabled,
  onDateScopeChange,
  onQueryChange,
  onStayScopeChange,
  onSubmit,
  query,
  stayScope,
}: VacationSearchControlsProps) {
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    onSubmit({ dateScope, query: query.trim(), stayScope })
  }

  return (
    <form className="grid gap-3 rounded-md border border-[#d7d9dc] bg-white p-4 shadow-[0_1px_2px_rgba(44,52,65,0.05)]" onSubmit={handleSubmit}>
      <div className="grid grid-cols-[auto_auto_minmax(220px,1fr)] items-center gap-3 max-[920px]:grid-cols-1">
        <SegmentedControl
          ariaLabel="Vacation search stay scope"
          disabled={disabled}
          onChange={onStayScopeChange}
          options={stayScopeOptions}
          value={stayScope}
        />

        <SegmentedControl
          ariaLabel="Vacation search date scope"
          disabled={disabled}
          onChange={onDateScopeChange}
          options={dateScopeOptions}
          value={dateScope}
        />

        <div className="flex min-h-10 min-w-0 items-stretch overflow-hidden rounded-md border border-[#c7cbd0] bg-white focus-within:border-[#8b929b]">
          <input
            aria-label="Search vacations"
            className="min-w-0 flex-1 border-0 bg-transparent px-3 text-[13px] leading-5 text-[#3f4650] outline-none placeholder:text-[#8a929c]"
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Search guest, party, Baz vacation reference, or RR reference"
            type="search"
            value={query}
          />
          <button
            className="min-w-[78px] border-0 border-l border-[#d7d9dc] bg-[#f6f7f8] px-4 text-[13px] font-semibold text-[#3f4650] hover:bg-[#eceff1] focus:bg-[#eceff1] focus:outline-none disabled:cursor-wait disabled:text-[#89919a]"
            disabled={disabled}
            type="submit"
          >
            Search
          </button>
        </div>
      </div>

      <p className="m-0 text-xs leading-[18px] text-[#6f7782]">
        Searches party name, traveller names, Baz vacation reference, and RR reference.
      </p>
    </form>
  )
}

function SegmentedControl<TValue extends string>({
  ariaLabel,
  disabled = false,
  onChange,
  options,
  value,
}: {
  ariaLabel: string
  disabled?: boolean
  onChange: (value: TValue) => void
  options: ReadonlyArray<{ label: string; value: TValue }>
  value: TValue
}) {
  return (
    <div aria-label={ariaLabel} className="inline-flex h-10 flex-wrap items-center gap-1 rounded-md bg-[#eef0f2] p-1" role="group">
      {options.map((option) => {
        const selected = option.value === value

        return (
          <button
            aria-pressed={selected}
            className={[
              "h-8 min-w-[76px] rounded px-3 text-xs font-semibold leading-4 transition",
              selected ? "bg-white text-[#2c3542] shadow-[0_1px_2px_rgba(44,52,65,0.12)]" : "bg-transparent text-[#66707c] hover:bg-[#f8f9fa]",
              disabled ? "cursor-wait opacity-70" : "",
            ].join(" ")}
            disabled={disabled}
            key={option.value}
            onClick={() => onChange(option.value)}
            type="button"
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
